import { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { Search, Activity, User, Shield, GraduationCap } from "lucide-react";

const roleTone = {
  admin: "bg-indigo-50 text-indigo-700",
  teacher: "bg-amber-50 text-amber-700",
  student: "bg-emerald-50 text-emerald-700",
};

const actionTone = (action) => {
  if (action?.includes("delete")) return "bg-rose-50 text-rose-700";
  if (action?.includes("create")) return "bg-emerald-50 text-emerald-700";
  if (action?.includes("update") || action?.includes("patch")) return "bg-sky-50 text-sky-700";
  if (action?.includes("login") || action?.includes("logout")) return "bg-violet-50 text-violet-700";
  if (action?.includes("attempt")) return "bg-amber-50 text-amber-700";
  return "bg-slate-100 text-slate-700";
};

const roleIcon = (r) => (r === "admin" ? Shield : r === "teacher" ? GraduationCap : User);

export default function AdminActivityLog() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [q, setQ] = useState("");
  const [role, setRole] = useState("");
  const [target, setTarget] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (role) params.set("actor_role", role);
      if (target) params.set("target_type", target);
      params.set("limit", "200");
      const [logRes, sumRes] = await Promise.all([
        api.get(`/activity-log?${params.toString()}`),
        api.get("/activity-log/summary"),
      ]);
      setItems(logRes.data);
      setSummary(sumRes.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [q, role, target]);

  const targetTypes = Array.from(new Set(items.map((i) => i.target_type).filter(Boolean)));

  return (
    <AppShell title="Activity log" subtitle="Everything that happens on the platform, in one audit trail.">
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="activity-summary">
          <div className="qs-card p-4">
            <p className="text-xs uppercase tracking-widest font-bold text-slate-500">Total events</p>
            <p className="font-display text-2xl font-black text-slate-900 mt-1" data-testid="activity-total">{summary.total}</p>
          </div>
          {summary.by_action.slice(0, 3).map((r) => (
            <div key={r.action} className="qs-card p-4">
              <p className="text-xs uppercase tracking-widest font-bold text-slate-500 truncate">{r.action}</p>
              <p className="font-display text-2xl font-black text-slate-900 mt-1">{r.count}</p>
            </div>
          ))}
        </div>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by actor name or action…"
            className="w-full bg-white border border-slate-200 rounded-xl pl-9 pr-4 py-2.5 focus:ring-2 focus:ring-indigo-500 outline-none"
            data-testid="activity-search-input"
          />
        </div>
        <select
          value={role}
          onChange={(e) => setRole(e.target.value)}
          className="bg-white border border-slate-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
          data-testid="activity-role-filter"
        >
          <option value="">All actors</option>
          <option value="admin">Admin</option>
          <option value="teacher">Teacher</option>
          <option value="student">Student</option>
        </select>
        <select
          value={target}
          onChange={(e) => setTarget(e.target.value)}
          className="bg-white border border-slate-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
          data-testid="activity-target-filter"
        >
          <option value="">All targets</option>
          {targetTypes.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
      </div>

      <div className="qs-card overflow-hidden" data-testid="activity-table">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-widest">
              <tr>
                <th className="text-left px-4 py-3 font-bold">Actor</th>
                <th className="text-left px-4 py-3 font-bold">Action</th>
                <th className="text-left px-4 py-3 font-bold">Target</th>
                <th className="text-left px-4 py-3 font-bold">When</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((row) => {
                const RoleIcon = roleIcon(row.actor_role);
                return (
                  <tr key={row.id} className="hover:bg-slate-50" data-testid={`activity-row-${row.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full grid place-items-center ${roleTone[row.actor_role] || "bg-slate-100 text-slate-600"}`}>
                          <RoleIcon className="w-4 h-4" />
                        </div>
                        <div>
                          <p className="font-semibold text-slate-800">{row.actor_name || "—"}</p>
                          <p className="text-[10px] uppercase tracking-widest text-slate-500">{row.actor_role || "system"}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${actionTone(row.action)}`}>
                        {row.action}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {row.target_type ? (
                        <span>
                          <span className="text-slate-800 font-semibold">{row.target_type}</span>
                          <span className="text-slate-400"> · </span>
                          <span className="text-xs text-slate-500">{(row.target_id || "").slice(0, 8)}</span>
                        </span>
                      ) : "—"}
                    </td>
                    <td className="px-4 py-3 text-slate-500">{row.created_at ? new Date(row.created_at).toLocaleString() : "—"}</td>
                  </tr>
                );
              })}
              {items.length === 0 && !loading && (
                <tr>
                  <td colSpan={4} className="text-center py-12 text-slate-500" data-testid="no-activity">
                    <Activity className="w-6 h-6 mx-auto mb-2 opacity-50" /> No activity matches these filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </AppShell>
  );
}
